'use client';

import { Editor } from '@tiptap/react';
import { Button } from '@workspace/ui/components/button';
import { Icon } from '@/src/components/ui';
import { cn } from '@workspace/ui/lib/utils';
import { colorTextPalette } from '@/src/constants/constants';
import { useState } from 'react';
import { useFloating } from '@floating-ui/react';

interface BottomToolbarProps {
  editor: Editor;
  children?: React.ReactNode;
}

export const BottomToolbar = ({ editor, children }: BottomToolbarProps) => {
  const [colorMenuOpen, setColorMenuOpen] = useState(false);
  const { refs, floatingStyles } = useFloating({ placement: 'top' });

  if (!editor) return null;

  const bold = editor.isActive('bold');
  const italic = editor.isActive('italic');
  const underline = editor.isActive('underline');

  return (
    <div className="flex items-center gap-1 border-t bg-white p-1 rounded-b-2xl justify-center">
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={cn('p-0 md:w-8 md:h-8 w-6 h-6', {
          'text-blue-primary': bold,
        })}
      >
        <Icon name="bold" className="size-3" />
      </Button>

      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={cn('p-0 md:w-8 md:h-8 w-6 h-6', {
          'text-blue-primary': italic,
        })}
      >
        <Icon name="italic" className="size-3" />
      </Button>

      <Button
        type="button"
        variant="ghost"
        size="icon"
        ref={refs.setReference}
        onClick={() => setColorMenuOpen((p) => !p)}
        className="p-0 md:w-8 md:h-8 w-6 h-6"
      >
        <Icon name="a" className="size-3" />
      </Button>

      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        className={cn('p-0 md:w-8 md:h-8 w-6 h-6', {
          'text-blue-primary': underline,
        })}
      >
        <Icon name="underline" className="size-4" />
      </Button>

      {colorMenuOpen && (
        <div
          ref={refs.setFloating}
          style={floatingStyles}
          className="mb-2 flex gap-0.5 bg-white p-1 border rounded-md"
        >
          {colorTextPalette.map(({ name, value }) => (
            <button
              key={name}
              className="size-6 rounded border"
              style={{ backgroundColor: value }}
              onClick={() => {
                editor.chain().focus().setColor(value).run();
                setColorMenuOpen(false);
              }}
            />
          ))}
        </div>
      )}
      <div className="rounded-sm items-center justify-center h-fit">
        {children}
      </div>
    </div>
  );
};
